import { Fragment, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Paperclip, Pencil } from 'lucide-react';
import { api, ApiError, qs } from '@/lib/api';
import { useActiveOrg } from '@/stores/auth';
import { usePermissions } from '@/hooks/usePermissions';
import { formatDate } from '@/lib/dates';
import { PageHeader } from '@/app/DashboardLayout';
import { Attachments } from '@/features/common/Attachments';
import { Button, Card, EmptyState, ErrorState, Money, SkeletonTable, TBody, TD, TH, THead, TR, Table } from '@/features/common/ui';
import { VendorSheet } from './VendorSheet';

interface Vendor {
  id: string;
  displayName: string;
  companyName: string | null;
  email: string | null;
  phone: string | null;
  gstin: string | null;
  pan: string | null;
  spend: { total: string; count: number; lastPaidOn: string | null };
}

interface ExpenseRow {
  id: string;
  date: string;
  categoryName: string | null;
  notes: string | null;
  referenceNumber: string | null;
  totalAmount: string;
  currency: string;
  receiptFileId: string | null;
  isRefund?: boolean;
  accountName: string | null;
}

/**
 * One vendor: who they are, what you have paid them with each bill, and - the
 * part that makes the page worth opening - the recorded expenses that look
 * like theirs but name no vendor yet, marked as theirs in one click.
 *
 * "Looks like theirs" is the same match a person makes by eye: the vendor's
 * name, or its first word ("Hostinger" in "Hostinger (Razorpay autopay)"), in
 * the expense's description or reference. Nothing is marked without a tick.
 */
export function VendorPage() {
  const { vendorId = '' } = useParams();
  const org = useActiveOrg();
  const perms = usePermissions();
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [openReceipt, setOpenReceipt] = useState<string | null>(null);
  const canEdit = perms.can('expense', 'edit');

  const vendor = useQuery({
    queryKey: ['vendors', org?.id, 'one', vendorId],
    queryFn: () => api.get<Vendor>(`/vendors/${vendorId}`),
    enabled: !!org?.id && !!vendorId,
  });
  const paid = useQuery({
    queryKey: ['expenses', org?.id, 'vendor', vendorId],
    queryFn: () => api.get<{ items: ExpenseRow[] }>(`/expenses${qs({ vendorId, limit: 200, sort: 'date', dir: 'desc' })}`),
    enabled: !!org?.id && !!vendorId,
  });

  // The word to look for: the name's first real word ("Hostinger" in
  // "Hostinger (Razorpay autopay)"), which is how a bank statement spells it.
  const firstWord = useMemo(() => (vendor.data?.displayName ?? '').split(/[\s(.,-]+/).find((w) => w.length >= 3) ?? '', [vendor.data?.displayName]);
  const suggestions = useQuery({
    queryKey: ['expenses', org?.id, 'vendor-suggestions', vendorId, firstWord],
    queryFn: () => api.get<{ items: ExpenseRow[] }>(`/expenses${qs({ vendorId: 'none', q: firstWord, limit: 100, sort: 'date', dir: 'desc' })}`),
    enabled: !!org?.id && !!firstWord && canEdit,
  });
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const suggestionRows = suggestions.data?.items ?? [];
  const allPicked = suggestionRows.length > 0 && suggestionRows.every((r) => picked.has(r.id));

  const refresh = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['expenses', org?.id] }),
      queryClient.invalidateQueries({ queryKey: ['vendors', org?.id] }),
    ]);
  };
  const mark = useMutation({
    mutationFn: (args: { ids: string[]; vendorId: string | null }) =>
      api.post('/expenses/bulk-classify', { expenseIds: args.ids, setVendor: { vendorId: args.vendorId } }),
    onSuccess: async () => {
      setPicked(new Set());
      await refresh();
    },
  });

  if (vendor.isError) {
    return (
      <div className="p-5">
        <ErrorState error={vendor.error} onRetry={() => vendor.refetch()} />
      </div>
    );
  }
  const v = vendor.data;

  return (
    <>
      <PageHeader
        title={v?.displayName ?? 'Vendor'}
        subtitle={v ? [v.companyName && v.companyName !== v.displayName ? v.companyName : null, v.gstin ? `GSTIN ${v.gstin}` : null, v.pan ? `PAN ${v.pan}` : null, v.email, v.phone].filter(Boolean).join(' · ') || 'Vendor' : undefined}
        actions={
          <div className="flex items-center gap-2">
            <Button asChild size="sm" variant="ghost">
              <Link to="/vendors">
                <ArrowLeft className="size-4" aria-hidden />
                Vendors
              </Link>
            </Button>
            {perms.can('vendor', 'edit') ? (
              <Button size="sm" variant="outline" onClick={() => setEditing(true)} data-testid="vendor-edit">
                <Pencil className="size-3.5" aria-hidden />
                Edit
              </Button>
            ) : null}
          </div>
        }
      />

      <div className="space-y-4 px-5 pb-8">
        <div className="grid gap-3 sm:grid-cols-3">
          <Card className="p-4">
            <p className="text-xs text-muted-foreground">Paid in total</p>
            <p className="mt-1 text-xl font-semibold" data-testid="vendor-total">
              {v ? <Money value={v.spend.total} currency="INR" /> : '…'}
            </p>
          </Card>
          <Card className="p-4">
            <p className="text-xs text-muted-foreground">Payments</p>
            <p className="mt-1 text-xl font-semibold" data-testid="vendor-count">{v?.spend.count ?? '…'}</p>
          </Card>
          <Card className="p-4">
            <p className="text-xs text-muted-foreground">Last paid</p>
            <p className="mt-1 text-xl font-semibold">{v?.spend.lastPaidOn ? formatDate(v.spend.lastPaidOn) : '—'}</p>
          </Card>
        </div>

        {canEdit && suggestionRows.length > 0 ? (
          <Card className="p-4" data-testid="vendor-suggestions">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div>
                <h2 className="text-sm font-semibold">These look like {v?.displayName}’s</h2>
                <p className="text-xs text-muted-foreground">
                  Expenses with “{firstWord}” in them and no vendor yet. Tick the ones that are theirs.
                </p>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setPicked(allPicked ? new Set() : new Set(suggestionRows.map((r) => r.id)))}
                >
                  {allPicked ? 'Clear' : 'Select all'}
                </Button>
                <Button
                  size="sm"
                  disabled={picked.size === 0}
                  loading={mark.isPending}
                  onClick={() => mark.mutate({ ids: [...picked], vendorId })}
                  data-testid="vendor-mark"
                >
                  Mark {picked.size || ''} as {v?.displayName}
                </Button>
              </div>
            </div>
            {mark.error instanceof ApiError ? (
              <p role="alert" className="mt-2 text-sm text-destructive">
                {mark.error.message}
              </p>
            ) : null}
            <ul className="mt-3 divide-y divide-border rounded-md border border-border">
              {suggestionRows.map((row) => (
                <li key={row.id}>
                  <label className="flex cursor-pointer items-center gap-3 px-3 py-2 text-sm hover:bg-accent/40">
                    <input
                      type="checkbox"
                      className="size-4 rounded border-input"
                      checked={picked.has(row.id)}
                      onChange={() =>
                        setPicked((current) => {
                          const next = new Set(current);
                          if (next.has(row.id)) next.delete(row.id);
                          else next.add(row.id);
                          return next;
                        })
                      }
                      data-testid="vendor-suggestion"
                    />
                    <span className="w-24 shrink-0 text-muted-foreground">{formatDate(row.date)}</span>
                    <span className="min-w-0 flex-1 truncate">{row.notes || row.referenceNumber || '—'}</span>
                    <span className="hidden text-xs text-muted-foreground sm:inline">{row.categoryName}</span>
                    <Money value={row.totalAmount} currency={row.currency} />
                  </label>
                </li>
              ))}
            </ul>
          </Card>
        ) : null}

        <Card>
          <div className="border-b border-border px-4 py-3">
            <h2 className="text-sm font-semibold">What you paid {v?.displayName ?? 'them'}</h2>
          </div>
          {paid.isError ? (
            <ErrorState error={paid.error} onRetry={() => paid.refetch()} />
          ) : paid.isLoading ? (
            <SkeletonTable rows={4} cols={4} />
          ) : paid.data?.items.length ? (
            <Table>
              <THead>
                <TR>
                  <TH>Date</TH>
                  <TH>Description</TH>
                  <TH>Category</TH>
                  <TH>Bill</TH>
                  <TH numeric>Amount</TH>
                  {canEdit ? <TH>{''}</TH> : null}
                </TR>
              </THead>
              <TBody>
                {paid.data.items.map((row) => (
                  <Fragment key={row.id}>
                    <TR data-testid="vendor-expense">
                      <TD className="whitespace-nowrap">{formatDate(row.date)}</TD>
                      <TD>
                        <span className="block max-w-sm truncate">{row.notes || '—'}</span>
                        {row.referenceNumber ? <span className="text-xs text-muted-foreground">{row.referenceNumber}</span> : null}
                      </TD>
                      <TD className="text-muted-foreground">{row.isRefund ? 'Refund' : row.categoryName}</TD>
                      <TD>
                        <button
                          type="button"
                          onClick={() => setOpenReceipt(openReceipt === row.id ? null : row.id)}
                          aria-expanded={openReceipt === row.id}
                          className="flex items-center gap-1.5 rounded px-1.5 py-0.5 text-xs hover:bg-accent"
                        >
                          <Paperclip className="size-3.5" aria-hidden />
                          {row.receiptFileId ? <span className="text-success">Attached</span> : <span className="text-muted-foreground">Add</span>}
                        </button>
                      </TD>
                      <TD numeric>
                        <Money value={row.totalAmount} currency={row.currency} />
                      </TD>
                      {canEdit ? (
                        <TD>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-muted-foreground"
                            onClick={() => mark.mutate({ ids: [row.id], vendorId: null })}
                            aria-label={`Not ${v?.displayName ?? 'this vendor'}'s: ${row.notes ?? row.date}`}
                          >
                            Not theirs
                          </Button>
                        </TD>
                      ) : null}
                    </TR>
                    {openReceipt === row.id ? (
                      <TR>
                        <TD colSpan={canEdit ? 6 : 5} className="bg-muted/20">
                          <Attachments
                            ownerType="expense"
                            ownerId={row.id}
                            title={`Bill from ${v?.displayName ?? 'the vendor'}`}
                            emptyHint="Drag their bill here, or paste a screenshot."
                          />
                        </TD>
                      </TR>
                    ) : null}
                  </Fragment>
                ))}
              </TBody>
            </Table>
          ) : (
            <EmptyState
              title="Nothing marked as theirs yet"
              description={
                suggestionRows.length
                  ? 'Tick their expenses above and mark them.'
                  : `When an expense is marked as ${v?.displayName ?? 'this vendor'}'s - here, or from the Expenses list - it shows here with its bill.`
              }
            />
          )}
        </Card>
      </div>

      {editing ? (
        <VendorSheet
          vendorId={vendorId}
          onClose={() => setEditing(false)}
          onSaved={async () => {
            setEditing(false);
            await refresh();
          }}
        />
      ) : null}
    </>
  );
}
